import { useTranslation } from 'react-i18next';

import {
	Box,
	Center,
	CheckCircleSolid,
	GiftWrapOutline,
	IconButton,
	Image,
	Popover,
	PopoverBody,
	PopoverContent,
	PopoverTrigger,
	rem,
	Text,
	VStack,
} from '@bonusly/design-system';
import { useCurrentUser } from '@bonusly/who-am-i';

import { DEFAULT_GIFT_WRAP_USES } from '../../../../config/recognition';
import { useDialogControl } from '../../../../lib/dialog';
import { getCollectibleImageUrl } from '../../../../modules/rewards/reward-details-modal/components/collectible-gallery-image';
import { usePostWrapReward } from '../../../../queries/rewards';
import { AvatarBubbleWrap } from '../../../post-wrap/avatar-bubble-wrap';
import { getActiveCollectibleItem } from '../../../post-wrap/collectible-inventory';
import { DecorationButton } from './decoration-button';

interface GiftWrapButtonProps {
	decorationVariant?: 'default' | 'list-item';
}

export const GiftWrapButton = ({ decorationVariant = 'default' }: GiftWrapButtonProps) => {
	const { t } = useTranslation('rewards', { keyPrefix: 'reward_details.postPurchaseCollectible' });
	const user = useCurrentUser();
	const rewardDialog = useDialogControl('reward');
	const { data: collectibleReward } = usePostWrapReward();
	const activeCollectibleItem = getActiveCollectibleItem(user);

	if (!activeCollectibleItem) {
		return (
			<DecorationButton
				label={t('addPostWrapTooltip')}
				onClick={() => collectibleReward && rewardDialog.onOpen({ rewardId: collectibleReward.id, skipPostPurchase: 'true' })}
				decorationVariant={decorationVariant}
			>
				<GiftWrapOutline />
			</DecorationButton>
		);
	}

	const isAvatarPattern = activeCollectibleItem.variant_data.type === 'avatar' || activeCollectibleItem.variant_data.pattern === 'avatar';
	const pattern = isAvatarPattern ? 'avatar' : (activeCollectibleItem.variant_data.pattern ?? 'stripes');
	const color = activeCollectibleItem.variant_data.color ?? 'teal';
	const imageUrl = isAvatarPattern ? undefined : getCollectibleImageUrl(pattern, color);
	const avatarName = [user?.first_name, user?.last_name].filter(Boolean).join(' ');

	if (decorationVariant === 'list-item') {
		return (
			<DecorationButton
				label={t('addPostWrapTooltip')}
				onClick={() => collectibleReward && rewardDialog.onOpen({ rewardId: collectibleReward.id, skipPostPurchase: 'true' })}
				decorationVariant="list-item"
			>
				<Box position="relative" display="inline-flex">
					<GiftWrapOutline />
					<Center position="absolute" top="-4px" right="-5px">
						<CheckCircleSolid boxSize="14px" color="primary.bg" />
					</Center>
				</Box>
			</DecorationButton>
		);
	}

	return (
		<Popover trigger="hover" placement="bottom" isLazy openDelay={300}>
			<PopoverTrigger>
				<Box>
					<IconButton
						aria-label={t('addPostWrapTooltip')}
						borderRadius={rem(10)}
						boxSize={rem(40)}
						color="secondary.fg"
						fontSize={rem(24)}
						_hover={{ background: 'secondary.bgAlpha' }}
						icon={
							<Box position="relative" display="inline-flex">
								<GiftWrapOutline />
								<Center position="absolute" top="-4px" right="-5px">
									<CheckCircleSolid boxSize="14px" color="primary.bg" />
								</Center>
							</Box>
						}
					/>
				</Box>
			</PopoverTrigger>
			<PopoverContent w="180px">
				<PopoverBody p={3}>
					<VStack spacing={3} align="flex-start">
						<Box borderRadius="md" overflow="hidden" w="full" h="100px">
							{isAvatarPattern ? (
								<AvatarBubbleWrap name={avatarName} src={user?.profile_pic_url} seed="gift-wrap-button" />
							) : (
								imageUrl && <Image src={imageUrl} alt={`${pattern} ${color} gift wrap`} w="full" h="full" objectFit="cover" />
							)}
						</Box>
						<Text fontSize="xs" color="text.secondary">
							{t('remainingCount', { count: activeCollectibleItem.remaining_uses, total: DEFAULT_GIFT_WRAP_USES })}
						</Text>
					</VStack>
				</PopoverBody>
			</PopoverContent>
		</Popover>
	);
};
